import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiCheck, FiX, FiSave, FiMove, FiEdit, FiDownload, FiInfo, FiAlertCircle, FiCheckCircle, FiLoader } from 'react-icons/fi';
import ResumeActionTooltip from '../../components/resumes/ResumeActionTooltip';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import DatePicker from '../../components/ui/DatePicker';
import InlineEditorWithSuggestions from '../../components/ui/InlineEditorWithSuggestions';
import { 
  draggable, 
  dropTargetForElements,
  monitorForElements 
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';
import { getSuggestions } from '../../data/suggestions';

// Suggestions and guidance system
const SUGGESTIONS = {
  summary: {
    minLength: 50,
    maxLength: 200,
    suggestions: [
      "Include your years of experience and key skills",
      "Mention your career goals or what you're looking for",
      "Use action verbs to describe your expertise"
    ]
  },
  experience: {
    minBullets: 3,
    maxBullets: 6,
    suggestions: [
      "Use action verbs (e.g., 'Developed', 'Managed', 'Implemented')",
      "Include quantifiable achievements with numbers",
      "Focus on results and impact, not just responsibilities",
      "Each bullet point should start with a strong action verb"
    ]
  },
  education: {
    minCourses: 2,
    maxCourses: 5,
    suggestions: [
      "Include 2-3 relevant courses for each major",
      "Focus on courses that relate to your target job",
      "Include advanced or specialized courses that showcase your expertise"
    ]
  },
  skills: {
    minSkills: 5,
    maxSkills: 15,
    suggestions: [
      "Mix technical and soft skills",
      "Include both hard skills (programming languages) and soft skills (leadership)",
      "Order skills by relevance to your target position"
    ]
  }
};

// Preloaded options for languages and proficiency
const LANGUAGE_OPTIONS = [
  'English','Mandarin Chinese','Spanish','Hindi','French','Arabic','Bengali','Russian','Portuguese','Urdu',
  'Indonesian','German','Japanese','Swahili','Marathi','Telugu','Turkish','Tamil','Vietnamese','Korean',
  'Italian','Polish','Ukrainian','Dutch','Persian','Malay','Thai','Greek','Hebrew','Czech',
  'Swedish','Finnish','Danish','Norwegian','Hungarian','Romanian','Filipino','Punjabi','Gujarati','Catalan'
];
const PROFICIENCY_LEVELS = [
  'Native','Bilingual','Fluent','Professional','Intermediate','Basic'
];

// Removed spell check function - replaced with suggestion system

// Progress indicator component
const ProgressIndicator = ({ formData }) => {
  const calculateProgress = () => {
    let completed = 0;
    let total = 0;
    
    // Summary
    total++;
    if (formData.summary && formData.summary.length > SUGGESTIONS.summary.minLength) completed++;
    
    // Experience
    total++;
    if (formData.experience && formData.experience.length > 0) {
      const hasGoodExperience = formData.experience.some(exp => 
        exp.details && exp.details.length >= SUGGESTIONS.experience.minBullets
      );
      if (hasGoodExperience) completed++;
    }
    
    // Education
    total++;
    if (formData.education && formData.education.length > 0) {
      const hasGoodEducation = formData.education.some(edu => 
        edu.courses && edu.courses.length >= SUGGESTIONS.education.minCourses
      );
      if (hasGoodEducation) completed++;
    }
    
    // Skills
    total++;
    if (formData.skills && formData.skills.length >= SUGGESTIONS.skills.minSkills) completed++;
    
    return Math.round((completed / total) * 100);
  };
  
  const progress = calculateProgress();
  
  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-primary-50 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-blue-900">Resume Completion</h3>
        <span className="text-sm font-semibold text-blue-700">{progress}%</span>
      </div>
      <div className="w-full bg-blue-200 rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-blue-500 to-primary-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      {progress < 100 && (
        <p className="text-xs text-blue-600 mt-2">
          Complete all sections for a stronger resume
        </p>
      )}
    </div>
  );
};

// Activity item with collapsed/expanded modes
const ActivityItem = ({ act, index, onUpdate, onRemove, onReorder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const editPanelRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (editPanelRef.current && !editPanelRef.current.contains(e.target)) {
        if (document.activeElement && editPanelRef.current.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isExpanded]);

  const dateText = () => {
    const s = act.start || '';
    const e = act.end || '';
    if (s && e) return `${s} - ${e}`;
    return s || e || '';
  };

  if (!isExpanded) {
    return (
      <DraggableItem
        dragId={`activity-${index}`}
        index={index}
        onReorder={onReorder}
        className="border border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 rounded-md p-4 transition-all group/act"
      >
        {() => (
          <div className="flex items-start">
            <div className="flex flex-col mr-3">
              <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200 mb-1">
                <FiMove className="h-4 w-4" />
              </div>
              <button 
                className="text-primary-600 opacity-0 group-hover/act:opacity-100 p-1 hover:bg-primary-50 rounded-lg transition-all duration-200 mb-1"
                onClick={(e) => { e.stopPropagation(); setIsExpanded(true); }}
                aria-label="Edit activity"
              >
                <FiEdit className="h-4 w-4" />
              </button>
              <button 
                className="text-red-500 opacity-0 group-hover/act:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all duration-200"
                onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                aria-label="Remove activity"
              >
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 cursor-pointer" onClick={() => setIsExpanded(true)}>
              <div className="flex justify-between items-start">
                <div className="flex-grow pr-8">
                  <h3 className="font-medium text-gray-900">{act.event || 'Activity/Organization'}</h3>
                </div>
                <p className="text-sm text-gray-600 flex-shrink-0 text-right">{dateText()}</p>
              </div>
              <div className="mt-2 text-gray-700">
                <ul className="ml-5 list-disc">
                  {(act.details || []).map((d, i) => (
                    <li key={i} className="mb-1">{d}</li>
                  ))}
                  {(!act.details || act.details.length === 0) && (
                    <li className="text-gray-400">Click to add details</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </DraggableItem>
    );
  }

  return (
    <div ref={editPanelRef} className="border border-gray-200 rounded-md p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InlineEditor
          value={act.event}
          onSave={(v) => onUpdate(index, { ...act, event: v })}
          className="w-full p-2 font-medium"
          placeholder="Activity or Organization"
        />
        <div className="flex gap-2">
          <DatePicker
            value={act.start}
            onChange={(v) => onUpdate(index, { ...act, start: v })}
            placeholder="Start"
            className="w-full"
          />
          <DatePicker
            value={act.end}
            onChange={(v) => onUpdate(index, { ...act, end: v })}
            placeholder="End"
            className="w-full"
          />
        </div>
      </div>
      <div className="mt-2">
        <BulletPointEditor
          items={act.details || []}
          onSave={(vals) => onUpdate(index, { ...act, details: vals })}
          onItemChange={(i, v) => {
            const details = [...(act.details || [])];
            details[i] = v;
            onUpdate(index, { ...act, details });
          }}
          onItemRemove={(i) => {
            const details = [...(act.details || [])];
            details.splice(i, 1);
            onUpdate(index, { ...act, details });
          }}
          onItemsReorder={(s, d) => {
            const details = [...(act.details || [])];
            const [r] = details.splice(s, 1);
            details.splice(d, 0, r);
            onUpdate(index, { ...act, details });
          }}
          placeholder="Add activity details"
        />
      </div>
    </div>
  );
};

// Activities section component
const ActivitiesSection = ({ activities, onUpdate }) => {
  const addActivity = useCallback(() => {
    onUpdate([...(activities || []), { event: 'New Activity', start: '', end: '', details: [] }]);
  }, [activities, onUpdate]);

  const updateActivity = useCallback((index, value) => {
    const updated = [...activities];
    updated[index] = value;
    onUpdate(updated);
  }, [activities, onUpdate]);

  const removeActivity = useCallback((index) => {
    const updated = [...activities];
    updated.splice(index, 1);
    onUpdate(updated);
  }, [activities, onUpdate]);

  const reorderActivity = useCallback((s, d) => {
    const updated = [...activities];
    const [removed] = updated.splice(s, 1);
    updated.splice(d, 0, removed);
    onUpdate(updated);
  }, [activities, onUpdate]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
          Activities
        </h2>
      </div>
      <DraggableContainer className="space-y-3">
        {(activities || []).map((act, index) => (
          <ActivityItem
            key={`activity-${index}-${act.event || 'new'}`}
            act={act}
            index={index}
            onUpdate={updateActivity}
            onRemove={removeActivity}
            onReorder={reorderActivity}
          />
        ))}
        <div className="flex justify-center">
          <button onClick={addActivity} className="flex items-center gap-1 bg-primary-600 text-white rounded-full px-4 py-1.5 text-sm hover:bg-primary-700 transition-all duration-200" aria-label="Add activity">
            <FiPlus className="h-4 w-4" /> Add Activity
          </button>
        </div>
      </DraggableContainer>
    </div>
  );
};

// Language item with collapsed/expanded modes
const LanguageItem = ({ item, index, onUpdate, onRemove, onReorder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const editPanelRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (editPanelRef.current && !editPanelRef.current.contains(e.target)) {
        if (document.activeElement && editPanelRef.current.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isExpanded]);

  if (!isExpanded) {
    return (
      <DraggableItem
        dragId={`lang-${index}`}
        index={index}
        onReorder={onReorder}
        className="border border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 rounded-md p-3 transition-all group/lang"
      >
        {() => (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200">
                <FiMove className="h-4 w-4" />
              </div>
              <span className="text-gray-800 font-medium">
                {(item.language || 'Language')}{item.level ? ` (${item.level})` : ''}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button className="text-primary-600 opacity-100 p-1 hover:bg-primary-50 rounded" onClick={() => setIsExpanded(true)} aria-label="Edit language">
                <FiEdit className="h-4 w-4" />
              </button>
              <button className="text-red-500 p-1 hover:bg-red-50 rounded" onClick={() => onRemove(index)} aria-label="Remove language">
                <FiTrash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </DraggableItem>
    );
  }

  return (
    <div ref={editPanelRef} className="border border-gray-200 rounded-md p-3">
      <div className="flex items-center gap-2">
        <select
          className="p-1 border rounded-lg flex-1 bg-white"
          value={item.language || ''}
          onChange={(e) => onUpdate(index, 'language', e.target.value)}
        >
          <option value="">Select language</option>
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <select
          className="p-1 border rounded-lg w-48 bg-white"
          value={item.level || ''}
          onChange={(e) => onUpdate(index, 'level', e.target.value)}
        >
          <option value="">Select level</option>
          {PROFICIENCY_LEVELS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Languages section component
const LanguagesSection = ({ languages, onUpdate }) => {
  const addLanguage = useCallback(() => {
    onUpdate([...(languages || []), { language: '', level: '' }]);
  }, [languages, onUpdate]);

  const updateLanguage = useCallback((index, key, value) => {
    const updated = [...languages];
    updated[index] = { ...(updated[index] || {}), [key]: value };
    onUpdate(updated);
  }, [languages, onUpdate]);

  const removeLanguage = useCallback((index) => {
    const updated = [...languages];
    updated.splice(index, 1);
    onUpdate(updated);
  }, [languages, onUpdate]);

  const reorderLanguage = useCallback((s, d) => {
    const updated = [...languages];
    const [r] = updated.splice(s, 1);
    updated.splice(d, 0, r);
    onUpdate(updated);
  }, [languages, onUpdate]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
          Languages
        </h2>
      </div>
      <DraggableContainer className="space-y-2">
        {(languages || []).map((item, index) => (
          <LanguageItem
            key={`lang-${index}-${item.language || 'new'}`}
            item={item}
            index={index}
            onUpdate={(i, k, v) => updateLanguage(i, k, v)}
            onRemove={removeLanguage}
            onReorder={reorderLanguage}
          />
        ))}
      </DraggableContainer>
      <div className="flex justify-center mt-2">
        <button onClick={addLanguage} className="flex items-center gap-1 bg-primary-600 text-white rounded-full px-4 py-1.5 text-sm hover:bg-primary-700 transition-all duration-200" aria-label="Add language">
          <FiPlus className="h-4 w-4" /> Add Language
        </button>
      </div>
    </div>
  );
};
// Suggestion component
const SuggestionBanner = ({ type, data, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  if (!isVisible) return null;
  
  const getSuggestion = () => {
    switch (type) {
      case 'summary':
        if (!data || data.length < SUGGESTIONS.summary.minLength) {
          return {
            icon: <FiAlertCircle className="h-4 w-4 text-yellow-500" />,
            message: `Your summary is too short. Aim for ${SUGGESTIONS.summary.minLength}-${SUGGESTIONS.summary.maxLength} characters.`,
            suggestions: SUGGESTIONS.summary.suggestions
          };
        }
        break;
      case 'experience':
        if (data && data.length > 0) {
          const expWithFewBullets = data.find(exp => 
            !exp.details || exp.details.length < SUGGESTIONS.experience.minBullets
          );
          if (expWithFewBullets) {
            return {
              icon: <FiAlertCircle className="h-4 w-4 text-yellow-500" />,
              message: `Add at least ${SUGGESTIONS.experience.minBullets} bullet points to each experience entry for better impact.`,
              suggestions: SUGGESTIONS.experience.suggestions
            };
          }
        }
        break;
      case 'education':
        if (data && data.length > 0) {
          const eduWithFewCourses = data.find(edu => 
            !edu.courses || edu.courses.length < SUGGESTIONS.education.minCourses
          );
          if (eduWithFewCourses) {
            return {
              icon: <FiAlertCircle className="h-4 w-4 text-yellow-500" />,
              message: `Consider adding ${SUGGESTIONS.education.minCourses}-${SUGGESTIONS.education.maxCourses} relevant courses to showcase your expertise.`,
              suggestions: SUGGESTIONS.education.suggestions
            };
          }
        }
        break;
      case 'skills':
        if (!data || data.length < SUGGESTIONS.skills.minSkills) {
          return {
            icon: <FiAlertCircle className="h-4 w-4 text-yellow-500" />,
            message: `Add at least ${SUGGESTIONS.skills.minSkills} skills to make your resume more comprehensive.`,
            suggestions: SUGGESTIONS.skills.suggestions
          };
        }
        break;
      default:
        return null;
    }
    return null;
  };
  
  const suggestion = getSuggestion();
  if (!suggestion) return null;
  
  return (
    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
      <div className="flex items-start">
        {suggestion.icon}
        <div className="ml-2 flex-1">
          <p className="text-sm text-yellow-800 font-medium">{suggestion.message}</p>
          {suggestion.suggestions && (
            <ul className="mt-1 text-xs text-yellow-700">
              {suggestion.suggestions.map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-1">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="ml-2 text-yellow-500 hover:text-yellow-700"
          aria-label="Dismiss"
        >
          <FiX className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Enhanced inline text editor with suggestions
const InlineEditor = ({ 
  value, 
  onSave, 
  onCancel, 
  className = "", 
  multiline = false, 
  placeholder = "Click to edit",
  fieldType = "general" // "skill", "school", "certificate", "general"
}) => {
  return (
    <InlineEditorWithSuggestions
      value={value}
      onSave={onSave}
      onCancel={onCancel}
      className={className}
      multiline={multiline}
      placeholder={placeholder}
      fieldType={fieldType}
      showSuggestions={fieldType !== "general"}
    />
  );
};

// Bullet point editor component that toggles between bulk edit and individual items
const BulletPointEditor = React.forwardRef(({ 
  items = [], 
  onSave, 
  onItemChange, 
  onItemRemove, 
  onItemsReorder,
  onAdd,
  addButtonLabel,
  dragIdPrefix = "detail",
  placeholder = "Add details"
}, ref) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [editValue, setEditValue] = useState('');
  const textareaRef = useRef(null);
  const cancelRef = useRef(false);
  const isSwitchingRef = useRef(false); // Prevents blur from racing with toggle click

  // Initialize edit value when entering bulk mode
  useEffect(() => {
    if (bulkMode) {
      setEditValue(items.join("\n"));
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [bulkMode, items]);

  const handleSave = () => {
    const newItems = editValue
      .split("\n")
      .map(item => item.trim())
      .filter(item => item.length > 0);
    
    onSave(newItems);
  };

  const handleCancel = () => {
    cancelRef.current = true;
    setEditValue(items.join("\n"));
  };

  const handleBulkToggle = (next) => {
    if (bulkMode && !next) {
      handleSave();
    }
    setBulkMode(next);
  };

  const handleBulkAddLine = useCallback(() => {
    setEditValue(prev => prev + (prev ? '\n' : ''));
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const len = textareaRef.current.value.length;
        textareaRef.current.setSelectionRange(len, len);
      }
    }, 0);
  }, []);

  const BulkToggle = () => (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1.5 text-sm text-gray-600">
        <span>Edit mode</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-medium ${!bulkMode ? 'text-gray-900' : 'text-gray-400'}`}>Per-bullet</span>
        <button
          type="button"
          role="switch"
          aria-checked={bulkMode}
          aria-label="Toggle between per-bullet and bulk edit mode"
          className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-0 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            bulkMode ? 'bg-primary-600' : 'bg-gray-300'
          }`}
          onMouseDown={() => {
            if (bulkMode) {
              isSwitchingRef.current = true; // Prevent textarea onBlur from saving (mousedown fires before blur)
              setTimeout(() => { isSwitchingRef.current = false; }, 0); // Reset if click doesn't complete (e.g. drag-away)
            }
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleBulkToggle(!bulkMode);
            isSwitchingRef.current = false;
          }}
        >
          <span
            className={`pointer-events-none absolute block rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out h-5 w-5 top-1 ${
              bulkMode ? 'left-8' : 'left-1'
            }`}
            aria-hidden="true"
          />
        </button>
        <span className={`text-sm font-medium ${bulkMode ? 'text-gray-900' : 'text-gray-400'}`}>Bulk</span>
        <div className="relative group/toggleinfo">
          <FiInfo className="h-3.5 w-3.5 text-gray-400 cursor-help" aria-hidden="true" />
          <div className="absolute bottom-full right-0 mb-1.5 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover/toggleinfo:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
            <b>Per-bullet:</b> edit each bullet individually<br/>
            <b>Bulk:</b> edit all bullets as text, one per line
          </div>
        </div>
      </div>
    </div>
  );

  if (bulkMode) {
    return (
      <div className="relative w-full">
        <BulkToggle />
        <div className="border border-gray-300 rounded-lg focus-within:border-primary-500 relative">
          <div className="absolute top-2 left-4 bottom-2 flex flex-col pointer-events-none">

          </div>
          <textarea
            ref={textareaRef}
            className="w-full border-none focus:ring-0 pl-8 py-2 pr-2"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => {
              if (cancelRef.current) {
                cancelRef.current = false;
                return;
              }
              // Skip save when switching via toggle - mousedown sets isSwitchingRef before blur fires
              if (isSwitchingRef.current) return;
              handleSave();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
            rows={Math.max(5, items.length + 2)}
            placeholder="Enter bullet points (one per line)"
            style={{ minHeight: '120px', width: '100%' }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Each line will become a separate bullet point
        </p>
        {addButtonLabel && (
          <button
            type="button"
            onClick={handleBulkAddLine}
            className="mt-3 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            aria-label="Add line in bulk"
          >
            <FiPlus className="-ml-0.5 mr-1 h-4 w-4" />
            {addButtonLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <BulkToggle />
      {items.length > 0 ? (
        <>
          {items.map((item, index) => (
            <DraggableItem
              key={dragIdPrefix + "-" + index + "-" + (item ? item.substring(0, 10) : "empty")}
              dragId={dragIdPrefix + "-" + index}
              index={index}
              onReorder={onItemsReorder}
              className="flex items-center mb-2 w-full"
            >
              {() => (
                <>
                  <span className="mr-2 text-gray-400">
                    <FiMove className="h-3 w-3" />
                  </span>
                  <span className="mr-2">•</span>
                  <div className="flex-1 min-w-0">
                    <InlineEditor
                      value={item}
                      onSave={(value) => onItemChange(index, value)}
                      multiline
                      className="w-full p-1.5"
                      placeholder="Add detail"
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onItemRemove(index);
                    }}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </>
              )}
            </DraggableItem>
          ))}
        </>
      ) : (
        <div className="text-gray-400 italic text-sm p-2">
          Add {placeholder}
        </div>
      )}
      {addButtonLabel && onAdd && !bulkMode && (
        <button
          type="button"
          onClick={onAdd}
          className="mt-3 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
        >
          <FiPlus className="-ml-0.5 mr-1 h-4 w-4" />
          {addButtonLabel}
        </button>
      )}
    </div>
  );
});

// Enhanced DraggableItem component with visual feedback and auto-scroll
const DraggableItem = ({ children, dragId, index, onReorder, className = "", useDragHandle = false }) => {
  const elementRef = useRef(null);
  const dragHandleRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const [dragPosition, setDragPosition] = useState(null); // 'above' or 'below'

  useEffect(() => {
    const element = elementRef.current;
    
    if (!element) return;

    // Auto-scroll setup
    const autoScrollCleanup = autoScrollForElements({
      element: element,
      getAllowedAxis: () => 'vertical'
    });

    // Make the element draggable (optionally with a drag handle)
    const dragOpts = {
      element: element,
      getInitialData: () => ({ index, dragId }),
      onDragStart: () => {
        setIsDragging(true);
      },
      onDrop: () => {
        setIsDragging(false);
      },
    };
    if (useDragHandle && dragHandleRef.current) {
      dragOpts.dragHandle = dragHandleRef.current;
    }
    const cleanup = draggable(dragOpts);

    // Make the container a drop target with visual feedback
    const dropCleanup = dropTargetForElements({
      element,
      getData: () => ({ index }),
      canDrop: ({ source }) => {
        if (source.data.index === index) {
          return false;
        }
        
        // Extract drag type prefix for matching.
        // Handles compound prefixes like "skill-group-0" → "skill-group"
        // and simple ones like "skill-0" → "skill", "experience-2" → "experience"
        const getTypePrefix = (id) => {
          const parts = id.split('-');
          // Keep all parts except the trailing numeric index
          const nonNumericParts = [];
          for (let i = 0; i < parts.length - 1; i++) {
            if (isNaN(Number(parts[i]))) nonNumericParts.push(parts[i]);
            else break;
          }
          return nonNumericParts.join('-') || parts[0];
        };
        
        const sourceType = getTypePrefix(source.data.dragId);
        const targetType = getTypePrefix(dragId);
        
        // For items within a specific parent (e.g., group-0-detail, project-0-detail),
        // ensure they stay within the same parent
        if (sourceType === targetType && sourceType.includes('-')) {
          const sourceParts = source.data.dragId.split('-');
          const targetParts = dragId.split('-');
          // Compare the parent index (first numeric part)
          const sourceParent = sourceParts.find(p => !isNaN(Number(p)));
          const targetParent = targetParts.find(p => !isNaN(Number(p)));
          return sourceParent === targetParent;
        }
        
        return sourceType === targetType;
      },
      onDragEnter: ({ location, source }) => {
        setIsDropTarget(true);
        
        const sourceIndex = source.data.index;
        const targetIndex = index;
        
        // Determine position based on index relationship
        if (sourceIndex < targetIndex) {
          // Dragging down - show indicator below
          setDragPosition('below');
        } else {
          // Dragging up - show indicator above  
          setDragPosition('above');
        }
      },
      onDragLeave: () => {
        setIsDropTarget(false);
        setDragPosition(null);
      },
      onDrop: ({ source, location }) => {
        setIsDropTarget(false);
        setDragPosition(null);
        
        const sourceIndex = source.data.index;
        const destinationIndex = index;

        if (sourceIndex !== destinationIndex) {
          onReorder(sourceIndex, destinationIndex);
        }
      },
    });

    return () => {
      cleanup();
      dropCleanup();
      autoScrollCleanup();
    };
  }, [index, dragId, onReorder]);

  // Visual classes for drag states
  const getElementClasses = () => {
    let classes = className || '';
    
    if (isDragging) {
      classes += ' opacity-50 scale-95 shadow-lg';
    }
    
    if (isDropTarget) {
      classes += ' bg-primary-50 border-primary-300';
    }
    
    return classes;
  };

  return (
    <div className="relative">
      {/* Drop indicator above */}
      {isDropTarget && dragPosition === 'above' && (
        <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary-500 rounded-full z-10">
          <div className="absolute -left-1 -top-1 w-2 h-2 bg-primary-500 rounded-full"></div>
          <div className="absolute -right-1 -top-1 w-2 h-2 bg-primary-500 rounded-full"></div>
        </div>
      )}
      
      <div 
        ref={elementRef} 
        className={getElementClasses() + " cursor-grab active:cursor-grabbing transition-all duration-200"}
      >
        {children(dragHandleRef)}
      </div>
      
      {/* Drop indicator below */}
      {isDropTarget && dragPosition === 'below' && (
        <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-500 rounded-full z-10">
          <div className="absolute -left-1 -top-1 w-2 h-2 bg-primary-500 rounded-full"></div>
          <div className="absolute -right-1 -top-1 w-2 h-2 bg-primary-500 rounded-full"></div>
        </div>
      )}
    </div>
  );
};

// Enhanced container component for sections that need auto-scroll
const DraggableContainer = ({ children, className = "" }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set up auto-scroll for the container
    const cleanup = autoScrollForElements({
      element: container,
      getAllowedAxis: () => 'vertical'
    });

    return cleanup;
  }, []);

  return (
    <div ref={containerRef} className={className}>
      {children}
    </div>
  );
};

// Skills section component (supports both flat list and grouped skills)
const SkillsSection = ({ skills, languages = [], onUpdate, onUpdateLanguages }) => {
  const [showInput, setShowInput] = useState(false);
  const [newSkill, setNewSkill] = useState('');

  const isGroupedFromSkills = React.useMemo(() => {
    if (!Array.isArray(skills) || skills.length === 0) {
      return false;
    }
    const allStrings = skills.every(skill => typeof skill === 'string');
    if (allStrings) {
      return false;
    }
    const allObjects = skills.every(skill => 
      skill && 
      typeof skill === 'object' && 
      skill.type && 
      Array.isArray(skill.details)
    );
    if (allObjects) {
      return true;
    }
    return skills.some(skill => 
      skill && 
      typeof skill === 'object' && 
      Array.isArray(skill.details)
    );
  }, [skills]);

  const [groupedMode, setGroupedMode] = useState(isGroupedFromSkills);
  const [skillsBulkMode, setSkillsBulkMode] = useState(false);
  const [skillsBulkValue, setSkillsBulkValue] = useState('');
  const [skillsBulkGroupValues, setSkillsBulkGroupValues] = useState({});
  const isGrouped = groupedMode;

  // Ensure we handle both old and new skills formats correctly
  const normalizedSkills = React.useMemo(() => {
    if (!Array.isArray(skills)) {
      return [];
    }
    
    if (skills.length === 0) {
      return [];
    }
    
    // Check if all items are strings (old format)
    const allStrings = skills.every(skill => typeof skill === 'string');
    if (allStrings) {
      return [{
        type: "Skills",
        details: skills
      }];
    }
    
    // Check if all items are objects with type and details (new format)
    const allObjects = skills.every(skill => 
      skill && 
      typeof skill === 'object' && 
      skill.type && 
      Array.isArray(skill.details)
    );
    
    if (allObjects) {
      // Ensure all details are strings to avoid rendering objects
      const validatedSkills = skills.map(skill => ({
        type: skill.type || 'Skills',
        details: Array.isArray(skill.details) ? skill.details.filter(detail => typeof detail === 'string' && detail !== null && detail !== undefined) : []
      }));
      return validatedSkills;
    }
    
    // Mixed format or problematic data - try to salvage what we can
    const salvaged = [];
    const stringSkills = [];
    
    skills.forEach(skill => {
      if (typeof skill === 'string') {
        stringSkills.push(skill);
      } else if (skill && typeof skill === 'object' && skill.type && Array.isArray(skill.details)) {
        salvaged.push({
          type: skill.type,
          details: skill.details.filter(detail => typeof detail === 'string' && detail !== null && detail !== undefined)
        });
      }
    });
    
    // If we found string skills, add them as a general group
    if (stringSkills.length > 0) {
      salvaged.unshift({
        type: "Skills",
        details: stringSkills
      });
    }
    
    return salvaged;
  }, [skills]);

  // Update grouped mode based on raw skills shape
  React.useEffect(() => {
    setGroupedMode(isGroupedFromSkills);
  }, [isGroupedFromSkills]);

  const COMMON_SOFT_SKILLS = ['Communication','Teamwork','Problem Solving','Time Management','Adaptability','Leadership','Critical Thinking','Collaboration','Attention to Detail','Creativity'];
  const COMMON_TECH_SKILLS = ['Python','SQL','Excel','PowerPoint','JavaScript','HTML','CSS','Git','React','AWS'];
  const [showLangQuickAdd, setShowLangQuickAdd] = useState(false);
  const [langChoice, setLangChoice] = useState('');
  const [profChoice, setProfChoice] = useState('');

  // Flat skills handlers (for non-grouped mode)
  const handleAddSkill = useCallback(() => {
    if (newSkill.trim()) {
      const updated = [...normalizedSkills];
      if (updated.length === 0) {
        updated.push({ type: "Skills", details: [newSkill.trim()] });
      } else {
        // Add to the first group regardless of its name
        updated[0] = { ...updated[0], details: [...(updated[0].details || []), newSkill.trim()] };
      }
      onUpdate(updated);
      setNewSkill('');
      setShowInput(false);
    }
  }, [newSkill, normalizedSkills, onUpdate]);

  const handleRemoveSkill = useCallback((index) => {
    const updated = [...normalizedSkills];
    if (updated.length > 0 && updated[0].details) {
      updated[0] = { ...updated[0], details: updated[0].details.filter((_, i) => i !== index) };
      onUpdate(updated);
    }
  }, [normalizedSkills, onUpdate]);

  const handleReorderSkills = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...normalizedSkills];
    if (updated.length > 0 && updated[0].details) {
      const details = [...updated[0].details];
      const [removed] = details.splice(sourceIndex, 1);
      details.splice(destinationIndex, 0, removed);
      updated[0] = { ...updated[0], details };
      onUpdate(updated);
    }
  }, [normalizedSkills, onUpdate]);

  const handleEditSkill = useCallback((index, value) => {
    const updated = [...normalizedSkills];
    if (updated.length > 0 && updated[0].details) {
      const details = [...updated[0].details];
      details[index] = value;
      updated[0] = { ...updated[0], details };
      onUpdate(updated);
    }
  }, [normalizedSkills, onUpdate]);

  // Language handlers
  const handleRemoveLanguage = useCallback((index) => {
    const updated = [...languages];
    updated.splice(index, 1);
    if (onUpdateLanguages) onUpdateLanguages(updated);
  }, [languages, onUpdateLanguages]);

  const handleReorderLanguages = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...languages];
    const [removed] = updated.splice(sourceIndex, 1);
    updated.splice(destinationIndex, 0, removed);
    if (onUpdateLanguages) onUpdateLanguages(updated);
  }, [languages, onUpdateLanguages]);

  const handleEditLanguage = useCallback((index, field, value) => {
    const updated = [...languages];
    updated[index] = { ...(updated[index] || {}), [field]: value };
    if (onUpdateLanguages) onUpdateLanguages(updated);
  }, [languages, onUpdateLanguages]);

  // Grouped skills handlers
  const addGroup = useCallback(() => {
    const updated = Array.isArray(normalizedSkills) ? [...normalizedSkills] : [];
    updated.push({ type: 'New Group', details: [] });
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const removeGroup = useCallback((groupIndex) => {
    const updated = [...normalizedSkills];
    updated.splice(groupIndex, 1);
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const reorderGroups = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...normalizedSkills];
    const [removed] = updated.splice(sourceIndex, 1);
    updated.splice(destinationIndex, 0, removed);
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const editGroupType = useCallback((groupIndex, value) => {
    const updated = [...normalizedSkills];
    updated[groupIndex] = { ...(updated[groupIndex] || {}), type: value };
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const addGroupDetail = useCallback((groupIndex) => {
    const updated = [...normalizedSkills];
    const group = { ...(updated[groupIndex] || { details: [] }) };
    group.details = [...(group.details || []), ''];
    updated[groupIndex] = group;
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const editGroupDetail = useCallback((groupIndex, detailIndex, value) => {
    const updated = [...normalizedSkills];
    const group = { ...(updated[groupIndex] || { details: [] }) };
    const details = [...(group.details || [])];
    details[detailIndex] = value;
    group.details = details;
    updated[groupIndex] = group;
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const removeGroupDetail = useCallback((groupIndex, detailIndex) => {
    const updated = [...normalizedSkills];
    const group = { ...(updated[groupIndex] || { details: [] }) };
    const details = [...(group.details || [])];
    details.splice(detailIndex, 1);
    group.details = details;
    updated[groupIndex] = group;
    onUpdate(updated);
  }, [normalizedSkills, onUpdate]);

  const handleSkillsBulkEnter = useCallback(() => {
    if (!isGrouped) {
      const allSkills = normalizedSkills.flatMap(g => g.details || []).filter(s => typeof s === 'string');
      setSkillsBulkValue(allSkills.join(', '));
    } else {
      const vals = {};
      normalizedSkills.forEach((g, i) => {
        vals[i] = (g.details || []).filter(s => typeof s === 'string').join(', ');
      });
      setSkillsBulkGroupValues(vals);
    }
    setSkillsBulkMode(true);
  }, [isGrouped, normalizedSkills]);

  const handleSkillsBulkSave = useCallback(() => {
    if (!isGrouped) {
      const items = skillsBulkValue.split(',').map(s => s.trim()).filter(s => s.length > 0);
      onUpdate(items);
    } else {
      const updated = normalizedSkills.map((g, i) => ({
        ...g,
        details: (skillsBulkGroupValues[i] || '').split(',').map(s => s.trim()).filter(s => s.length > 0)
      }));
      onUpdate(updated);
    }
    setSkillsBulkMode(false);
  }, [isGrouped, skillsBulkValue, skillsBulkGroupValues, normalizedSkills, onUpdate]);

  const reorderGroupDetails = useCallback((groupIndex, sourceIndex, destinationIndex) => {
    const updated = [...normalizedSkills];
    const group = { ...(updated[groupIndex] || { details: [] }) };
    const details = [...(group.details || [])];
    const [removed] = details.splice(sourceIndex, 1);
    details.splice(destinationIndex, 0, removed);
    group.details = details;
    updated[groupIndex] = group;
    onUpdate(updated);
  }, [skills, onUpdate]);

  const handleGroupingToggle = useCallback((toGrouped) => {
    if (skillsBulkMode) handleSkillsBulkSave();
    setGroupedMode(toGrouped);
    if (toGrouped) {
      const flat = normalizedSkills
        .flatMap(group => Array.isArray(group?.details) ? group.details : [])
        .filter(item => typeof item === 'string' && item.trim().length > 0);
      onUpdate(flat.length ? [{ type: 'Skills', details: flat }] : [{ type: 'Skills', details: [] }]);
    } else {
      const flat = normalizedSkills
        .flatMap(group => Array.isArray(group?.details) ? group.details : [])
        .filter(item => typeof item === 'string' && item.trim().length > 0);
      onUpdate(flat);
    }
  }, [skillsBulkMode, handleSkillsBulkSave, normalizedSkills, onUpdate]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
          Skills
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600">Grouping</span>
            <div className="relative inline-block w-12 align-middle select-none">
              <input type="checkbox" className="sr-only" checked={groupedMode} onChange={(e) => handleGroupingToggle(e.target.checked)} />
              <label
                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-all duration-200 ease-in-out ${groupedMode ? 'bg-primary-600' : 'bg-gray-200'}`}
                onClick={() => handleGroupingToggle(!groupedMode)}
              >
                <span className={`absolute block rounded-full bg-white shadow h-4 w-4 top-1 transition-transform duration-200 ease-in-out ${groupedMode ? 'right-1' : 'left-1'}`}></span>
              </label>
            </div>
          </div>
          <div className="w-px h-4 bg-gray-300"></div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-600">Bulk</span>
            <div className="relative group/bulkinfo">
              <FiInfo className="h-3 w-3 text-gray-400 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover/bulkinfo:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Edit all skills as comma-separated text
              </div>
            </div>
            <div className="relative inline-block w-12 align-middle select-none">
              <input type="checkbox" className="sr-only" checked={skillsBulkMode} onChange={() => {}} />
              <label
                className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-all duration-200 ease-in-out ${skillsBulkMode ? 'bg-primary-600' : 'bg-gray-200'}`}
                onClick={() => skillsBulkMode ? handleSkillsBulkSave() : handleSkillsBulkEnter()}
              >
                <span className={`absolute block rounded-full bg-white shadow h-4 w-4 top-1 transition-transform duration-200 ease-in-out ${skillsBulkMode ? 'right-1' : 'left-1'}`}></span>
              </label>
            </div>
          </div>
          {isGrouped && !skillsBulkMode && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50"
                onClick={() => {
                  let groups = Array.isArray(skills) ? [...skills] : [];
                  if (!isGrouped) {
                    const flat = (skills || []).filter(s => typeof s === 'string');
                    groups = flat.length ? [{ type: 'Skills', details: flat }] : [{ type: 'Skills', details: [] }];
                  }
                  const idx = groups.findIndex(g => (g?.type || '').toLowerCase() === 'soft skills');
                  const details = idx >= 0 ? new Set(groups[idx].details || []) : new Set();
                  COMMON_SOFT_SKILLS.forEach(s => details.add(s));
                  if (idx >= 0) {
                    groups[idx] = { ...groups[idx], type: 'Soft Skills', details: Array.from(details) };
                  } else {
                    groups.push({ type: 'Soft Skills', details: Array.from(details) });
                  }
                  setGroupedMode(true);
                  onUpdate(groups);
                }}
                aria-label="Add Soft Skills"
              >Add Soft Skills</button>
              <button
                type="button"
                className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50"
                onClick={() => {
                  let groups = Array.isArray(skills) ? [...skills] : [];
                  if (!isGrouped) {
                    const flat = (skills || []).filter(s => typeof s === 'string');
                    groups = flat.length ? [{ type: 'Skills', details: flat }] : [{ type: 'Skills', details: [] }];
                  }
                  const idx = groups.findIndex(g => (g?.type || '').toLowerCase() === 'technical skills' || (g?.type || '').toLowerCase() === 'tech skills');
                  const details = idx >= 0 ? new Set(groups[idx].details || []) : new Set();
                  COMMON_TECH_SKILLS.forEach(s => details.add(s));
                  if (idx >= 0) {
                    groups[idx] = { ...groups[idx], type: 'Technical Skills', details: Array.from(details) };
                  } else {
                    groups.push({ type: 'Technical Skills', details: Array.from(details) });
                  }
                  setGroupedMode(true);
                  onUpdate(groups);
                }}
                aria-label="Add Technical Skills"
              >Add Technical Skills</button>
              <button
                type="button"
                className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50"
                onClick={() => {
                  const updated = Array.isArray(languages) ? [...languages] : [];
                  updated.push({ language: '', level: '' });
                  if (onUpdateLanguages) onUpdateLanguages(updated);
                }}
                aria-label="Add Language"
              >Add Language</button>
            </div>
          )}
        </div>
      </div>

      {skillsBulkMode ? (
        !isGrouped ? (
          <div className="space-y-2">
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:border-primary-500 focus:ring-0 focus:outline-none"
              value={skillsBulkValue}
              onChange={(e) => setSkillsBulkValue(e.target.value)}
              onBlur={handleSkillsBulkSave}
              rows={4}
              placeholder="Enter skills separated by commas (e.g., Python, JavaScript, React, SQL)"
              autoFocus
            />
            <p className="text-xs text-gray-500">Separate each skill with a comma</p>
          </div>
        ) : (
          <div className="space-y-3">
            {normalizedSkills.map((group, gIndex) => (
              <div key={`bulk-group-${gIndex}`} className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-700 mb-1.5">{group.type || 'Group'}</div>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-primary-500 focus:ring-0 focus:outline-none"
                  value={skillsBulkGroupValues[gIndex] || ''}
                  onChange={(e) => setSkillsBulkGroupValues(prev => ({ ...prev, [gIndex]: e.target.value }))}
                  onBlur={handleSkillsBulkSave}
                  rows={2}
                  placeholder="Enter skills separated by commas"
                />
              </div>
            ))}
            <p className="text-xs text-gray-500">Separate each skill with a comma</p>
          </div>
        )
      ) : !isGrouped ? (
        <DraggableContainer className="flex flex-wrap gap-2 min-h-[40px] p-2">
          {(normalizedSkills[0]?.details || []).filter(skill => typeof skill === 'string').map((skill, index) => (
            <DraggableItem
              key={`skill-${index}-${String(skill)}`}
              dragId={`skill-${index}`}
              index={index}
              onReorder={handleReorderSkills}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1.5 rounded-full text-sm group/skill relative flex items-center transition-all"
            >
              {() => (
                <>
                  <span className="mr-2 text-gray-400">
                    <FiMove className="h-3 w-3" />
                  </span>
                  <InlineEditor
                    value={skill}
                    onSave={(value) => handleEditSkill(index, value)}
                    className="bg-transparent border-none p-0"
                    fieldType="skills"
                  />
                  <button 
                    className="ml-1.5 text-red-500 opacity-0 group-hover/skill:opacity-100 hover:text-red-700 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSkill(index);
                    }}
                  >
                    <FiTrash2 className="h-3 w-3" />
                  </button>
                </>
              )}
            </DraggableItem>
          ))}
          
          {!showInput ? (
            <button
              className="w-8 h-8 bg-gray-100 hover:bg-primary-100 text-gray-600 hover:text-primary-600 rounded-full flex items-center justify-center text-sm transition-all duration-200"
              onClick={() => setShowInput(true)}
            >
              <FiPlus className="h-3 w-3" />
            </button>
          ) : (
            <div className="relative">
              <input
                type="text"
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm min-w-32 focus:border-primary-500 focus:ring-0 focus:outline-none"
                placeholder="Add skill"
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSkill();
                  if (e.key === 'Escape') setShowInput(false);
                }}
                onBlur={() => {
                  if (newSkill.trim()) handleAddSkill();
                  else setShowInput(false);
                }}
                autoFocus
              />
              {newSkill.length > 0 && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                  {getSuggestions(newSkill, 'skills').map((suggestion, index) => (
                    <div
                      key={index}
                      className="px-3 py-2 cursor-pointer text-sm hover:bg-gray-100 text-gray-900"
                      onClick={() => {
                        setNewSkill(suggestion);
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DraggableContainer>
      ) : (
        <div className="space-y-3">
          <DraggableContainer className="space-y-3">
            {normalizedSkills.map((group, gIndex) => (
              <DraggableItem
                key={`skill-group-${gIndex}-${group.type || 'group'}`}
                dragId={`skill-group-${gIndex}`}
                index={gIndex}
                onReorder={reorderGroups}
                className="border border-gray-200 rounded-lg p-3"
                useDragHandle
              >
                {(handleRef) => (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div ref={handleRef} className="p-1 text-gray-400 mr-2 cursor-grab active:cursor-grabbing"><FiMove className="h-4 w-4" /></div>
                        <InlineEditor
                          value={group.type || ''}
                          onSave={(v) => editGroupType(gIndex, v)}
                          className="font-medium p-1.5 flex-grow"
                          placeholder="Group name (e.g., Tech Skills)"
                        />
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeGroup(gIndex); }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="mt-2">
                      {(group.details || []).length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {(group.details || []).filter(item => typeof item === 'string').map((item, dIndex) => (
                            <DraggableItem
                              key={`group-${gIndex}-detail-${dIndex}-${String(item)}`}
                              dragId={`group-${gIndex}-detail-${dIndex}`}
                              index={dIndex}
                              onReorder={(s, d) => reorderGroupDetails(gIndex, s, d)}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1.5 rounded-full text-sm group/detail relative flex items-center transition-all"
                            >
                              {() => (
                                <>
                                  <span className="mr-2 text-gray-400"><FiMove className="h-3 w-3" /></span>
                                  <InlineEditor
                                    value={item}
                                    onSave={(v) => editGroupDetail(gIndex, dIndex, v)}
                                    className="bg-transparent border-none p-0"
                                  />
                                  <button
                                    className="ml-1.5 text-red-500 opacity-0 group-hover/detail:opacity-100 hover:text-red-700 transition-opacity"
                                    onClick={(e) => { e.stopPropagation(); removeGroupDetail(gIndex, dIndex); }}
                                  >
                                    <FiTrash2 className="h-3 w-3" />
                                  </button>
                                </>
                              )}
                            </DraggableItem>
                          ))}
                        </div>
                      ) : (
                        <div className="text-gray-400 italic text-sm">No items added yet</div>
                      )}
                      <button
                        onClick={() => addGroupDetail(gIndex)}
                        className="mt-2 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <FiPlus className="-ml-0.5 mr-1 h-3 w-3" /> Add Item
                      </button>
                    </div>
                  </div>
                )}
              </DraggableItem>
            ))}
          </DraggableContainer>
          <button
            onClick={addGroup}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiPlus className="-ml-0.5 mr-1 h-4 w-4" /> Add Group
          </button>
        </div>
      )}
      
      {/* Languages Section */}
      {languages && languages.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
            <span className="w-1.5 h-3 bg-primary-400 rounded-sm mr-2"></span>
            Languages
          </h3>
          <DraggableContainer className="flex flex-wrap gap-2">
            {languages.map((lang, index) => (
              <DraggableItem
                key={`language-${index}-${lang.language || 'new'}`}
                dragId={`language-${index}`}
                index={index}
                onReorder={handleReorderLanguages}
                className="bg-primary-100 hover:bg-primary-200 text-primary-800 px-3 py-1.5 rounded-full text-sm group/lang relative flex items-center transition-all gap-2"
              >
                {() => (
                  <>
                    <span className="mr-2 text-primary-400">
                      <FiMove className="h-3 w-3" />
                    </span>
                    <select
                      value={lang.language || ''}
                      onChange={(e) => handleEditLanguage(index, 'language', e.target.value)}
                      className="bg-transparent border-none text-primary-800 text-sm focus:outline-none appearance-none pr-4"
                      style={{ minWidth: '80px' }}
                    >
                      <option value="">Language</option>
                      {LANGUAGE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {lang.language && (
                      <>
                        <span className="text-primary-600">-</span>
                        <select
                          value={lang.level || ''}
                          onChange={(e) => handleEditLanguage(index, 'level', e.target.value)}
                          className="bg-transparent border-none text-primary-600 text-sm focus:outline-none appearance-none pr-4"
                          style={{ minWidth: '70px' }}
                        >
                          <option value="">Level</option>
                          {PROFICIENCY_LEVELS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </>
                    )}
                    <button 
                      className="ml-1.5 text-primary-600 opacity-0 group-hover/lang:opacity-100 hover:text-primary-800 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLanguage(index);
                      }}
                    >
                      <FiTrash2 className="h-3 w-3" />
                    </button>
                    <button 
                      className="ml-1.5 text-primary-600 opacity-0 group-hover/lang:opacity-100 hover:text-primary-800 transition-opacity"
                      title="Add another language"
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = Array.isArray(languages) ? [...languages] : [];
                        updated.splice(index + 1, 0, { language: '', level: '' });
                        if (onUpdateLanguages) onUpdateLanguages(updated);
                      }}
                    >
                      <FiPlus className="h-3 w-3" />
                    </button>
                  </>
                )}
              </DraggableItem>
            ))}
          </DraggableContainer>
        </div>
      )}
    </div>
  );
};

// Experience item component

const ExperienceItem = ({ experience, index, onUpdate, onRemove, onReorder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const editPanelRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (editPanelRef.current && !editPanelRef.current.contains(e.target)) {
        if (document.activeElement && editPanelRef.current.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isExpanded]);

  const handleChange = useCallback((field, value) => {
    onUpdate(index, { ...experience, [field]: value });
  }, [experience, index, onUpdate]);

  const handleDetailChange = useCallback((detailIndex, value) => {
    const updated = { ...experience };
    if (!updated.details) updated.details = [];
    updated.details[detailIndex] = value;
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const addDetail = useCallback(() => {
    const updated = { ...experience };
    if (!updated.details) updated.details = [];
    updated.details.push('');
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const removeDetail = useCallback((detailIndex) => {
    const updated = { ...experience };
    if (updated.details) {
      updated.details.splice(detailIndex, 1);
      onUpdate(index, updated);
    }
  }, [experience, index, onUpdate]);

  const handleReorderDetails = useCallback((sourceIndex, destinationIndex) => {
    const updated = { ...experience };
    if (updated.details) {
      const [removed] = updated.details.splice(sourceIndex, 1);
      updated.details.splice(destinationIndex, 0, removed);
      onUpdate(index, updated);
    }
  }, [experience, index, onUpdate]);

  // Project handlers
  const handleProjectChange = useCallback((projectIndex, field, value) => {
    const updated = { ...experience };
    if (!updated.projects) updated.projects = [];
    if (!updated.projects[projectIndex]) updated.projects[projectIndex] = {};
    updated.projects[projectIndex] = {
      ...updated.projects[projectIndex],
      [field]: value
    };
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const handleProjectDetailChange = useCallback((projectIndex, detailIndex, value) => {
    const updated = { ...experience };
    if (!updated.projects[projectIndex].details) {
      updated.projects[projectIndex].details = [];
    }
    const updatedDetails = [...updated.projects[projectIndex].details];
    updatedDetails[detailIndex] = value;
    updated.projects[projectIndex] = {
      ...updated.projects[projectIndex],
      details: updatedDetails
    };
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const addProject = useCallback(() => {
    const updated = { ...experience };
    if (!updated.projects) updated.projects = [];
    updated.projects.push({
      title: 'New Project',
      description: '',
      details: ['']
    });
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const removeProject = useCallback((projectIndex) => {
    const updated = { ...experience };
    if (updated.projects) {
      updated.projects.splice(projectIndex, 1);
      onUpdate(index, updated);
    }
  }, [experience, index, onUpdate]);

  const addProjectDetail = useCallback((projectIndex) => {
    const updated = { ...experience };
    if (!updated.projects || !updated.projects[projectIndex]) return;
    if (!updated.projects[projectIndex].details) {
      updated.projects[projectIndex].details = [];
    }
    updated.projects[projectIndex] = {
      ...updated.projects[projectIndex],
      details: [...updated.projects[projectIndex].details, '']
    };
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const removeProjectDetail = useCallback((projectIndex, detailIndex) => {
    const updated = { ...experience };
    if (!updated.projects || !updated.projects[projectIndex] || !updated.projects[projectIndex].details) {
      return;
    }
    const updatedDetails = [...updated.projects[projectIndex].details];
    updatedDetails.splice(detailIndex, 1);
    updated.projects[projectIndex] = {
      ...updated.projects[projectIndex],
      details: updatedDetails
    };
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  const handleReorderProjects = useCallback((sourceIndex, destinationIndex) => {
    const updated = { ...experience };
    if (updated.projects) {
      const [removed] = updated.projects.splice(sourceIndex, 1);
      updated.projects.splice(destinationIndex, 0, removed);
      onUpdate(index, updated);
    }
  }, [experience, index, onUpdate]);

  const handleReorderProjectDetails = useCallback((projectIndex, sourceIndex, destinationIndex) => {
    const updated = { ...experience };
    if (updated.projects && updated.projects[projectIndex] && updated.projects[projectIndex].details) {
      const [removed] = updated.projects[projectIndex].details.splice(sourceIndex, 1);
      updated.projects[projectIndex].details.splice(destinationIndex, 0, removed);
      onUpdate(index, updated);
    }
  }, [experience, index, onUpdate]);

  // Convert to/from project-based
  const toggleProjectMode = useCallback((hasProjects) => {
    const updated = { ...experience };
    
    if (hasProjects) {
      // Converting to project-based - move details to first project
      if (!updated.projects || updated.projects.length === 0) {
        updated.projects = [{
          title: `${updated.position} Project`,
          description: '',
          details: [...(updated.details || [])]
        }];
        updated.details = [];
      }
    } else {
      // Converting from project-based - move all project details to experience details
      const allProjectDetails = [];
      if (updated.projects) {
        updated.projects.forEach(project => {
          if (project.details && project.details.length > 0) {
            allProjectDetails.push(...project.details);
          }
        });
      }
      updated.details = [
        ...(updated.details || []),
        ...allProjectDetails
      ];
    }
    
    updated.has_projects = hasProjects;
    onUpdate(index, updated);
  }, [experience, index, onUpdate]);

  if (isExpanded) {
    return (
      <div ref={editPanelRef} className="border border-gray-200 rounded-md p-4 space-y-4">
        <div className="flex justify-between">
          <div className="space-y-3 flex-grow pr-12 w-full">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Position Title</label>
              <InlineEditor
                value={experience.position}
                onSave={(value) => handleChange('position', value)}
                className="w-full p-2 font-medium"
                placeholder="Position Title"
                fieldType="position"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
              <InlineEditor
                value={experience.company}
                onSave={(value) => handleChange('company', value)}
                className="w-full p-2"
                placeholder="Company Name"
                fieldType="organization"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Location</label>
              <InlineEditor
                value={experience.location}
                onSave={(value) => handleChange('location', value)}
                className="w-full p-2"
                placeholder="Location"
              />
            </div>
          </div>
          
          <div className="space-y-3 flex-shrink-0" style={{ minWidth: '200px' }}>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <DatePicker
                value={experience.start}
                onChange={(value) => handleChange('start', value)}
                placeholder="Select start date"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <DatePicker
                value={experience.end}
                onChange={(value) => handleChange('end', value)}
                placeholder="Select end date"
                className="w-full"
              />
            </div>
          </div>
        </div>
        
        {/* Project Mode Toggle */}
        <div className="flex items-center mt-4">
          <label className="block text-sm font-medium text-gray-700 mr-4">
            Project-Based Experience
          </label>
          <div className="relative inline-block w-12 align-middle select-none">
            <input
              type="checkbox"
              className="sr-only"
              checked={experience.has_projects || false}
              onChange={(e) => toggleProjectMode(e.target.checked)}
            />
            <label
              className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-all duration-200 ease-in-out ${
                experience.has_projects ? 'bg-primary-600' : 'bg-gray-200'
              }`}
              onClick={() => toggleProjectMode(!experience.has_projects)}
            >
              <span
                className={`absolute block rounded-full bg-white shadow h-4 w-4 top-1 transition-transform duration-200 ease-in-out ${
                  experience.has_projects ? 'right-1' : 'left-1'
                }`}
              ></span>
            </label>
          </div>
        </div>
        
        {/* Content based on mode */}
        {!experience.has_projects ? (
          // Regular details mode
          <div>
            <div className="flex items-center gap-2 mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Responsibilities & Achievements
              </label>

            </div>
            
            <div className="mt-2 space-y-2 w-full">
              {/* Bullet points section with conditional rendering */}
              <BulletPointEditor
                items={experience.details || []}
                onSave={(values) => {
                  const updated = { ...experience };
                  updated.details = values;
                  onUpdate(index, updated);
                }}
                onItemChange={handleDetailChange}
                onItemRemove={removeDetail}
                onItemsReorder={handleReorderDetails}
                onAdd={addDetail}
                addButtonLabel="Add Detail"
                placeholder="Add details"
              />
            </div>
          </div>
        ) : (
          // Projects mode
          <div>
            <label className="block text-sm font-medium text-gray-700 mt-4">
              Projects
            </label>
            
            <div className="mt-2 space-y-4">
              {(experience.projects || []).map((project, pIndex) => (
                <DraggableItem
                  key={`project-${pIndex}-${project.title || 'new'}`}
                  dragId={`project-${pIndex}`}
                  index={pIndex}
                  onReorder={handleReorderProjects}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  {() => (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center flex-1">
                          <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200 mr-2">
                            <FiMove className="h-4 w-4" />
                          </div>
                          <InlineEditor
                            value={project.title}
                            onSave={(value) => handleProjectChange(pIndex, 'title', value)}
                            className="font-medium p-1.5 flex-grow"
                            placeholder="Project Title"
                          />
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeProject(pIndex);
                          }}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <InlineEditor
                        value={project.description}
                        onSave={(value) => handleProjectChange(pIndex, 'description', value)}
                        className="w-full p-1.5 italic text-sm mb-2"
                        placeholder="Project description"
                      />
                      
                      <label className="block text-xs font-medium text-gray-700 mt-2">
                        Project Details
                      </label>
                      
                      <div className="mt-1 space-y-2 w-full">
                        {/* Project bullet points with BulletPointEditor */}
                        <BulletPointEditor
                          items={project.details || []}
                          onSave={(values) => {
                            handleProjectChange(pIndex, 'details', values);
                          }}
                          onItemChange={(detailIndex, value) => handleProjectDetailChange(pIndex, detailIndex, value)}
                          onItemRemove={(detailIndex) => removeProjectDetail(pIndex, detailIndex)}
                          onItemsReorder={(sourceIndex, destinationIndex) => 
                            handleReorderProjectDetails(pIndex, sourceIndex, destinationIndex)
                          }
                          onAdd={() => addProjectDetail(pIndex)}
                          addButtonLabel="Add Detail"
                          dragIdPrefix={`project-detail-${pIndex}`}
                          placeholder="Add project details"
                        />
                      </div>
                    </div>
                  )}
                </DraggableItem>
              ))}
            </div>
            
            <button
              onClick={addProject}
              className="mt-3 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiPlus className="-ml-0.5 mr-1 h-4 w-4" />
              Add Project
            </button>
          </div>
        )}
        
      </div>
    );
  }

  return (
    <DraggableItem
      dragId={`experience-${index}`}
      index={index}
      onReorder={onReorder}
      className="border border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 rounded-md p-4 transition-all group/exp"
    >
      {() => (
        <div className="flex items-start">
          <div className="flex flex-col mr-3">
            <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200 mb-1">
              <FiMove className="h-4 w-4" />
            </div>
            <button 
              className="text-primary-600 opacity-0 group-hover/exp:opacity-100 p-1 hover:bg-primary-50 rounded-lg transition-all duration-200 mb-1"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
            >
              <FiEdit className="h-4 w-4" />
            </button>
            <button 
              className="text-red-500 opacity-0 group-hover/exp:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
          
          <div 
            className="flex-1 cursor-pointer" 
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-grow pr-8">
                <h3 className="font-medium text-gray-900">{experience.position || 'Position'}</h3>
                <p className="text-gray-700">
                  {experience.company || 'Company'}
                  {experience.location ? `, ${experience.location}` : ''}
                </p>
              </div>
              <p className="text-sm text-gray-600 flex-shrink-0 text-right">
                {experience.start || 'Start Date'}
                {experience.end ? ` - ${experience.end}` : ' - Present'}
              </p>
            </div>
            
            {/* Display content based on mode */}
            {!experience.has_projects ? (
              <div className="mt-2 text-gray-700">
                {/* Simple bullet list in collapsed view */}
                <ul className="ml-5 list-disc">
                  {(experience.details || []).map((detail, i) => (
                    <li key={i} className="mb-1">{detail}</li>
                  ))}
                  {(!experience.details || experience.details.length === 0) && (
                    <li className="text-gray-400">Click to add details</li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="mt-2">
                {(experience.projects || []).map((project, pIndex) => (
                  <div key={pIndex} className="mb-3 pl-2 border-l-2 border-gray-200">
                    <h4 className="text-gray-800 font-medium">{project.title || 'Project Title'}</h4>
                    {project.description ? 
                      <p className="text-gray-700 italic text-sm">{project.description}</p> : 
                      <p className="text-gray-400 italic text-sm">Project description</p>
                    }
                    {/* Simple bullet list for project details */}
                    <ul className="mt-1 ml-5 list-disc text-gray-700">
                      {(project.details || []).map((detail, i) => (
                        <li key={i} className="mb-1">{detail}</li>
                      ))}
                      {(!project.details || project.details.length === 0) && (
                        <li className="text-gray-400">Project details</li>
                      )}
                    </ul>
                  </div>
                ))}
                {(!experience.projects || experience.projects.length === 0) && (
                  <div className="text-gray-400 italic text-sm">No projects added yet</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </DraggableItem>
  );
};

// Experience section component
const ExperienceSection = ({ experience, onUpdate }) => {
  const handleAddExperience = useCallback(() => {
    const newExp = {
      position: "New Position",
      company: "Company",
      location: "",
      start: "",
      end: "",
      details: []
    };
    onUpdate([...experience, newExp]);
  }, [experience, onUpdate]);

  const handleUpdateExperience = useCallback((index, updatedExp) => {
    const updated = [...experience];
    updated[index] = updatedExp;
    onUpdate(updated);
  }, [experience, onUpdate]);

  const handleRemoveExperience = useCallback((index) => {
    const updated = [...experience];
    updated.splice(index, 1);
    onUpdate(updated);
  }, [experience, onUpdate]);

  const handleReorderExperience = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...experience];
    const [removed] = updated.splice(sourceIndex, 1);
    updated.splice(destinationIndex, 0, removed);
    onUpdate(updated);
  }, [experience, onUpdate]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
          Experience
        </h2>
        <div className="group relative">
          <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
            Focus on achievements and quantifiable results
            <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>
      
      <DraggableContainer className="space-y-4">
        {experience.map((exp, index) => (
          <ExperienceItem
            key={`exp-${index}-${exp.position || 'new'}`}
            experience={exp}
            index={index}
            onUpdate={handleUpdateExperience}
            onRemove={handleRemoveExperience}
            onReorder={handleReorderExperience}
          />
        ))}
        
        {experience.length === 0 && (
          <p className="text-gray-500 italic text-sm text-center p-4 border border-dashed border-gray-300 rounded-md">
            Click the "Add Experience" button below to add work experience
          </p>
        )}
        
        <div className="flex justify-center mt-4">
          <button
            className="flex items-center gap-1 bg-primary-600 text-white rounded-full px-4 py-1.5 text-sm hover:bg-primary-700 transition-all duration-200"
            onClick={handleAddExperience}
          >
            <FiPlus className="h-4 w-4" /> Add Experience
          </button>
        </div>
      </DraggableContainer>
    </div>
  );
};

// Education Item Component
const EducationItem = ({ education, index, onUpdate, onRemove, onReorder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const editPanelRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (editPanelRef.current && !editPanelRef.current.contains(e.target)) {
        if (document.activeElement && editPanelRef.current.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isExpanded]);

  const handleChange = useCallback((field, value) => {
    const updatedEducation = { ...education, [field]: value };
    onUpdate(index, updatedEducation);
  }, [education, index, onUpdate]);

  if (isExpanded) {
    return (
      <div ref={editPanelRef} className="border border-gray-200 rounded-md p-4 space-y-4">
        <div className="flex justify-between">
          <div className="space-y-3 flex-grow pr-8">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">School/University</label>
              <InlineEditor
                value={education.school}
                onSave={(value) => handleChange('school', value)}
                className="w-full p-2 font-medium"
                placeholder="School Name"
                fieldType="schools"
              />
            </div>
            
            <div className="flex space-x-3">
              <div className="flex-grow">
                <label className="block text-xs font-medium text-gray-500 mb-1">Degree/Major</label>
                <InlineEditor
                  value={education.major}
                  onSave={(value) => handleChange('major', value)}
                  className="w-full p-2"
                  placeholder="Major"
                />
              </div>
              
              <div className="w-1/3">
                <label className="block text-xs font-medium text-gray-500 mb-1">GPA</label>
                <InlineEditor
                  value={education.gpa}
                  onSave={(value) => handleChange('gpa', value)}
                  className="w-full p-2"
                  placeholder="GPA"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Minor (Optional)</label>
              <InlineEditor
                value={education.minor}
                onSave={(value) => handleChange('minor', value)}
                className="w-full p-2"
                placeholder="Minor (Optional)"
              />
            </div>
            
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className="block text-xs font-medium text-gray-500">Relevant Courses (Optional)</label>
                <div className="group relative">
                  <FiInfo className="h-3 w-3 text-gray-400 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    Add 2-3 relevant courses to showcase your expertise
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {education.courses && education.courses.map((course, courseIndex) => (
                  <div key={courseIndex} className="flex items-center space-x-2">
                    <InlineEditor
                      value={course}
                      onSave={(value) => {
                        const updatedCourses = [...(education.courses || [])];
                        updatedCourses[courseIndex] = value;
                        handleChange('courses', updatedCourses);
                      }}
                      className="flex-1 p-2 text-sm"
                      placeholder="Course name"
                      fieldType="courses"
                    />
                    <button
                      onClick={() => {
                        const updatedCourses = [...(education.courses || [])];
                        updatedCourses.splice(courseIndex, 1);
                        handleChange('courses', updatedCourses);
                      }}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const updatedCourses = [...(education.courses || []), ''];
                    handleChange('courses', updatedCourses);
                  }}
                  className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                >
                  <FiPlus className="h-4 w-4" /> Add Course
                </button>
              </div>
            </div>
          </div>
          
          <div className="space-y-3 flex-shrink-0" style={{ minWidth: '200px' }}>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
              <DatePicker
                value={education.start}
                onChange={(value) => handleChange('start', value)}
                placeholder="Select start date"
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
              <DatePicker
                value={education.end}
                onChange={(value) => handleChange('end', value)}
                placeholder="Select end date"
                className="w-full"
              />
            </div>
          </div>
        </div>
        
      </div>
    );
  }

  return (
    <DraggableItem
      dragId={`education-${index}`}
      index={index}
      onReorder={onReorder}
      className="border border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 rounded-md p-4 transition-all group/edu"
    >
      {() => (
        <div className="flex items-start">
          <div className="flex flex-col mr-3">
            <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200 mb-1">
              <FiMove className="h-4 w-4" />
            </div>
            <button 
              className="text-primary-600 opacity-0 group-hover/edu:opacity-100 p-1 hover:bg-primary-50 rounded-lg transition-all duration-200 mb-1"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
            >
              <FiEdit className="h-4 w-4" />
            </button>
            <button 
              className="text-red-500 opacity-0 group-hover/edu:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
          
          <div 
            className="flex-1 cursor-pointer" 
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-grow pr-8">
                <h3 className="font-medium text-gray-900">{education.school || 'School Name'}</h3>
                <p className="text-gray-700">
                  {education.major || 'Major'}
                  {education.minor ? `, Minor in ${education.minor}` : ''}
                  {education.gpa ? ` | GPA: ${education.gpa}` : ''}
                </p>
                {education.courses && education.courses.length > 0 && (
                  <p className="text-sm text-gray-600 italic mt-1">
                    <em>Relevant Courses: {education.courses.join(', ')}</em>
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-600 flex-shrink-0 text-right">
                {education.start || 'Start Date'}
                {education.end ? ` - ${education.end}` : ' - End Date'}
              </p>
            </div>
          </div>
        </div>
      )}
    </DraggableItem>
  );
};

// Education Section Component
const EducationSection = ({ education, onUpdate }) => {
  const handleAddEducation = useCallback(() => {
    const newEdu = {
      school: "New School",
      major: "Major",
      minor: "",
      gpa: "",
      start: "",
      end: "",
      courses: []
    };
    onUpdate([...education, newEdu]);
  }, [education, onUpdate]);

  const handleUpdateEducation = useCallback((index, updatedEdu) => {
    const updated = [...education];
    updated[index] = updatedEdu;
    onUpdate(updated);
  }, [education, onUpdate]);

  const handleRemoveEducation = useCallback((index) => {
    const updated = [...education];
    updated.splice(index, 1);
    onUpdate(updated);
  }, [education, onUpdate]);

  // Continuing from the previous file...

const handleReorderEducation = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...education];
    const [removed] = updated.splice(sourceIndex, 1);
    updated.splice(destinationIndex, 0, removed);
    onUpdate(updated);
  }, [education, onUpdate]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
          Education
        </h2>
        <div className="group relative">
          <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
            Include relevant courses and achievements
            <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      </div>
      
      <DraggableContainer className="space-y-3">
        {education.map((edu, index) => (
          <EducationItem
            key={`edu-${index}-${edu.school || 'new'}-${edu.major || 'major'}-${index}`}
            education={edu}
            index={index}
            onUpdate={handleUpdateEducation}
            onRemove={handleRemoveEducation}
            onReorder={handleReorderEducation}
          />
        ))}
        
        {education.length === 0 && (
          <p className="text-gray-500 italic text-sm text-center p-4 border border-dashed border-gray-300 rounded-md">
            Click the "Add Education" button below to add your education
          </p>
        )}
        
        <div className="flex justify-center mt-4">
          <button
            className="flex items-center gap-1 bg-primary-600 text-white rounded-full px-4 py-1.5 text-sm hover:bg-primary-700 transition-all duration-200"
            onClick={handleAddEducation}
          >
            <FiPlus className="h-4 w-4" /> Add Education
          </button>
        </div>
      </DraggableContainer>
    </div>
  );
};

// Certificate Item Component
const CertificateItem = ({ certificate, index, onUpdate, onRemove, onReorder }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const editPanelRef = useRef(null);

  useEffect(() => {
    if (!isExpanded) return;
    const handleMouseDown = (e) => {
      if (editPanelRef.current && !editPanelRef.current.contains(e.target)) {
        if (document.activeElement && editPanelRef.current.contains(document.activeElement)) {
          document.activeElement.blur();
        }
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [isExpanded]);

  const handleChange = useCallback((field, value) => {
    onUpdate(index, { ...certificate, [field]: value });
  }, [certificate, index, onUpdate]);

  if (isExpanded) {
    return (
      <div ref={editPanelRef} className="border border-gray-200 rounded-md p-4 space-y-4">
        <div className="flex justify-between">
          <div className="space-y-3 flex-grow pr-8">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Certificate Name</label>
              <InlineEditor
                value={certificate.certificate}
                onSave={(value) => handleChange('certificate', value)}
                className="w-full p-2 font-medium"
                placeholder="Certificate Name"
                fieldType="certificates"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Issuing Organization</label>
              <InlineEditor
                value={certificate.issuer}
                onSave={(value) => handleChange('issuer', value)}
                className="w-full p-2"
                placeholder="Issuing Organization"
                fieldType="organization"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Issue Date</label>
            <DatePicker
              value={certificate.start}
              onChange={(value) => handleChange('start', value)}
              placeholder="Select issue date"
              className="w-full"
            />
          </div>
        </div>
        
      </div>
    );
  }

  return (
    <DraggableItem
      dragId={`certificate-${index}`}
      index={index}
      onReorder={onReorder}
      className="border border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 rounded-md p-4 transition-all group/cert"
    >
      {() => (
        <div className="flex items-start">
          <div className="flex flex-col mr-3">
            <div className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 duration-200 mb-1">
              <FiMove className="h-4 w-4" />
            </div>
            <button 
              className="text-primary-600 opacity-0 group-hover/cert:opacity-100 p-1 hover:bg-primary-50 rounded-lg transition-all duration-200 mb-1"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true);
              }}
            >
              <FiEdit className="h-4 w-4" />
            </button>
            <button 
              className="text-red-500 opacity-0 group-hover/cert:opacity-100 p-1 hover:bg-red-50 rounded-lg transition-all duration-200"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
          
          <div 
            className="flex-1 cursor-pointer" 
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-grow pr-8">
                <h3 className="font-medium text-gray-900">{certificate.certificate || 'Certificate Name'}</h3>
                <p className="text-gray-700">{certificate.issuer || 'Issuing Organization'}</p>
              </div>
              <p className="text-sm text-gray-600 flex-shrink-0 text-right">
                {certificate.start || 'Issue Date'}
              </p>
            </div>
          </div>
        </div>
      )}
    </DraggableItem>
  );
};

// Certificates Section Component
const CertificatesSection = ({ certificates, onUpdate }) => {
  const handleAddCertificate = useCallback(() => {
    const newCert = {
      certificate: "New Certification",
      issuer: "",
      start: ""
    };
    onUpdate([...certificates, newCert]);
  }, [certificates, onUpdate]);

  const handleUpdateCertificate = useCallback((index, updatedCert) => {
    const updated = [...certificates];
    updated[index] = updatedCert;
    onUpdate(updated);
  }, [certificates, onUpdate]);

  const handleRemoveCertificate = useCallback((index) => {
    const updated = [...certificates];
    updated.splice(index, 1);
    onUpdate(updated);
  }, [certificates, onUpdate]);

  const handleReorderCertificates = useCallback((sourceIndex, destinationIndex) => {
    const updated = [...certificates];
    const [removed] = updated.splice(sourceIndex, 1);
    updated.splice(destinationIndex, 0, removed);
    onUpdate(updated);
  }, [certificates, onUpdate]);

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-300 pb-1 mb-3 flex items-center">
        <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
        Certifications
      </h2>
      
      <DraggableContainer className="space-y-3">
        {certificates.map((cert, index) => (
          <CertificateItem
            key={`cert-${index}-${cert.certificate || 'new'}`}
            certificate={cert}
            index={index}
            onUpdate={handleUpdateCertificate}
            onRemove={handleRemoveCertificate}
            onReorder={handleReorderCertificates}
          />
        ))}
        
        {certificates.length === 0 && (
          <p className="text-gray-500 italic text-sm text-center p-4 border border-dashed border-gray-300 rounded-md">
            Click the "Add Certificate" button below to add certifications
          </p>
        )}
        
        <div className="flex justify-center mt-4">
          <button
            className="flex items-center gap-1 bg-primary-600 text-white rounded-full px-4 py-1.5 text-sm hover:bg-primary-700 transition-all duration-200"
            onClick={handleAddCertificate}
          >
            <FiPlus className="h-4 w-4" /> Add Certificate
          </button>
        </div>
      </DraggableContainer>
    </div>
  );
};

// Main Resume Document Component
const ResumeDocument = forwardRef(({ resumeId, versionId, isEmbedded = false, onSaveSuccess }, ref) => {
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [activeVersion, setActiveVersion] = useState(null);
  const [versionName, setVersionName] = useState('');
  const [showActionTooltip, setShowActionTooltip] = useState(false);
  const [showSectionOrderModal, setShowSectionOrderModal] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [formData, setFormData] = useState({
    summary: '',
    name: '',
    email: '',
    phone: '',
    location: '',
    links: [],
    skills: [],
    education: [],
    experience: [],
    certificates: [],
    activities: [],
    languages: [],
    sectionOrder: ['education', 'skills', 'experience', 'activities', 'certificates'] // Default section order
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showLinkAdd, setShowLinkAdd] = useState(false);
  const [newLink, setNewLink] = useState('');
  const [editingVersionName, setEditingVersionName] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchResumeVersion();
  }, [resumeId, versionId]);
  
  // Ensure section order exists
  useEffect(() => {
    if (formData && !formData.sectionOrder) {
      setFormData(prev => ({
        ...prev,
        sectionOrder: ['education', 'skills', 'experience', 'activities', 'certificates']
      }));
    }
  }, [formData]);

  // Ensure newly added sections are present in sectionOrder (no languages here; shown in Skills)
  useEffect(() => {
    if (!formData?.sectionOrder) return;
    const fullOrder = ['education', 'skills', 'experience', 'activities', 'certificates'];
    const updated = [...formData.sectionOrder];
    let changed = false;
    fullOrder.forEach((s) => {
      if (!updated.includes(s)) {
        updated.push(s);
        changed = true;
      }
    });
    if (changed) {
      setFormData(prev => ({ ...prev, sectionOrder: updated }));
    }
  }, [formData?.sectionOrder]);

  // Format date for version name
  const formatVersionDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original on error
    }
  };

  const fetchResumeVersion = async () => {
    try {
      setLoading(true);
      // If no specific versionId is provided, get the first (original) version
      const versionResponse = versionId 
        ? await resumeApi.getResumeVersion(resumeId, versionId)
        : await resumeApi.getFirstResumeVersion(resumeId); 
      
      if (versionResponse && versionResponse.data) {
        setActiveVersion(versionResponse.data);
        setFormData(versionResponse.data.content);
        
        // Use custom name if available, otherwise create a meaningful name from version ID and date
        if (versionResponse.data.customName) {
          setVersionName(versionResponse.data.customName);
        } else {
          const versionIdPart = (versionResponse.data.versionId || '').split('_')[0];
          const createdDate = versionResponse.data.createdAt;
          
          if (createdDate) {
            setVersionName(`Version ${versionIdPart} (${formatVersionDate(createdDate)})`);
          } else {
            setVersionName(`Version ${versionIdPart}`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching resume version:', error);
      console.error('Resume ID:', resumeId, 'Version ID:', versionId);
      if (error.response && error.response.status === 404) {
        toast.error(`Resume version not found. Please check if the resume exists.`);
      } else {
        toast.error('Failed to load resume version');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Ensure sectionOrder exists before saving
      const dataToSave = { ...formData };
      if (!dataToSave.sectionOrder) {
        dataToSave.sectionOrder = ['skills', 'experience', 'education', 'certificates', 'activities'];
      }
      
      await resumeApi.updateVersionContent(
        resumeId, 
        activeVersion.versionId, 
        { content: dataToSave }
      );
      
      setLastSaved(new Date());
      setShowActionTooltip(true);
      toast.success('Resume updated successfully');
      
      // Call onSaveSuccess callback if provided (for embedded mode)
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      
    } catch (error) {
      console.error('Error saving resume:', error);
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({ save: handleSave, hasUnsavedChanges: false }), [handleSave]);
  
  const handleVersionNameSave = async (newName) => {
    try {
      await resumeApi.renameVersion(resumeId, activeVersion.versionId, newName);
      setVersionName(newName);
      setEditingVersionName(false);
      toast.success('Version name updated');
    } catch (error) {
      console.error('Error updating version name:', error);
      toast.error('Failed to update version name');
    }
  };

  // Update handlers
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const addLink = useCallback(() => {
    if (newLink.trim()) {
      setFormData(prev => ({
        ...prev,
        links: [...prev.links, newLink.trim()]
      }));
      setNewLink('');
      setShowLinkAdd(false);
    }
  }, [newLink]);

  const updateLink = useCallback((index, value) => {
    setFormData(prev => {
      const updated = [...prev.links];
      updated[index] = value;
      return { ...prev, links: updated };
    });
  }, []);

  const removeLink = useCallback((index) => {
    setFormData(prev => {
      const updated = [...prev.links];
      updated.splice(index, 1);
      return { ...prev, links: updated };
    });
  }, []);

  const formatLinkForDisplay = (link) => {
    let displayText = link.replace(/^https?:\/\//, '').replace(/^www\./, '');
    return displayText.length > 30 ? displayText.substring(0, 27) + '...' : displayText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!activeVersion) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Resume version not found</h2>
        <p className="mt-2 text-gray-600">The resume version you're looking for does not exist.</p>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow-lg border border-gray-200 rounded-lg max-w-6xl mx-auto ${isEmbedded ? 'my-0 p-4' : 'my-8 p-8'}`}>
      {/* Version Name and Save Button */}
      {!isEmbedded ? (
        <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          {editingVersionName ? (
            <div className="flex items-center">
              <input
                type="text"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm mr-2"
                autoFocus
              />
              <button
                onClick={() => handleVersionNameSave(versionName)}
                className="text-green-600 hover:text-green-800 mr-1"
              >
                <FiCheck className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setEditingVersionName(false);
                  setVersionName(activeVersion.customName || (activeVersion.versionId || '').split('_')[0]);
                }}
                className="text-red-600 hover:text-red-800"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center">
              <span 
                className="text-sm font-medium text-gray-700 mr-2 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                title={versionName} // Show full name on hover
              >
                Version: {versionName}
              </span>
              <button
                onClick={() => setEditingVersionName(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiEdit className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          
          {lastSaved && (
            <p className="text-xs text-gray-500 ml-2">
              Last saved: {lastSaved.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStyleModal(true)}
            className="inline-flex items-center px-4 py-2 border border-primary-300 text-primary-700 shadow-sm text-sm font-medium rounded-lg hover:bg-primary-50 transition-all duration-200 hover:shadow-sm"
          >
            <FiDownload className="-ml-1 mr-2 h-5 w-5" />
            Download PDF
          </button>
          <button
            onClick={() => setShowSectionOrderModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 shadow-sm text-sm font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
          >
            <FiMove className="-ml-1 mr-2 h-5 w-5" />
            Re-order Sections
          </button>
          {!isEmbedded && (
            <button
              onClick={() => navigate('/jobs')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 shadow-sm text-sm font-medium rounded-lg hover:bg-gray-50 transition-all duration-200"
            >
              Apply for Jobs
            </button>
          )}
          <button
            onClick={() => setShowActionTooltip(true)}
            className="inline-flex items-center px-4 py-2 border border-emerald-500 text-emerald-600 shadow-sm text-sm font-medium rounded-lg hover:bg-emerald-50 transition-all duration-200"
          >
            <FiCheck className="-ml-1 mr-2 h-5 w-5" />
            Looks Good!
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg transition-all duration-200 text-white bg-primary-600 hover:bg-primary-700"
          >
            <FiSave className="-ml-1 mr-2 h-5 w-5" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      ) : (
        /* Embedded mode header with save button */
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Resume</h3>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {saving ? (
              <>
                <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <FiSave className="-ml-1 mr-2 h-4 w-4" />
                Save
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="text-center mb-8 pb-4 border-b border-gray-200">
        <InlineEditor
          value={formData.name}
          onSave={(value) => updateField('name', value)}
          className="text-3xl font-bold text-gray-900 text-center px-2 py-1 inline-block rounded"
          placeholder="Your Name"
        />
        
        <div className="flex justify-center items-center space-x-4 mt-2 text-sm text-gray-600">
          <InlineEditor
            value={formData.email}
            onSave={(value) => updateField('email', value)}
            className="px-2 py-1 rounded"
            placeholder="Email"
          />
          
          <span className="text-gray-400">•</span>
          
          <InlineEditor
            value={formData.phone}
            onSave={(value) => updateField('phone', value)}
            className="px-2 py-1 rounded"
            placeholder="Phone"
          />
          
          <span className="text-gray-400">•</span>
          
          <InlineEditor
            value={formData.location}
            onSave={(value) => updateField('location', value)}
            className="px-2 py-1 rounded"
            placeholder="Location"
          />
        </div>
        
        {/* Links */}
        <div className="flex justify-center flex-wrap items-center mt-3 text-sm">
          {formData.links?.map((link, index) => (
            <div key={index} className="group relative mx-1 mb-1">
              <span className="text-primary-600 hover:text-primary-800 hover:bg-primary-50 px-2 py-1 rounded-lg transition-all duration-200 inline-flex items-center">
                <span className="w-3 h-3 bg-primary-600 rounded-full mr-1.5"></span>
                <InlineEditor
                  value={link}
                  onSave={(value) => updateLink(index, value)}
                  className="bg-transparent border-none p-0 text-primary-600 hover:text-primary-800"
                  placeholder="Edit link"
                />
              </span>
              <button 
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => removeLink(index)}
              >
                ×
              </button>
            </div>
          ))}
          
          <div className="relative ml-2">
            <button
              className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
              onClick={() => setShowLinkAdd(!showLinkAdd)}
            >
              <FiPlus className="h-4 w-4" />
            </button>
            
            {showLinkAdd && (
              <div className="absolute top-full mt-1 left-0 z-10 bg-white shadow-lg rounded-md p-2 w-64 border">
                <div className="flex items-center">
                  <input
                    type="text"
                    className="border border-gray-300 rounded-l-md px-3 py-1 text-sm focus:border-primary-500 focus:ring-0 flex-grow"
                    placeholder="Add link (e.g., linkedin.com/in/...)"
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addLink();
                      if (e.key === 'Escape') setShowLinkAdd(false);
                    }}
                    autoFocus
                  />
                  <button
                    className="bg-primary-600 text-white rounded-r-md px-2 py-1 text-sm hover:bg-primary-700 transition-all duration-200"
                    onClick={addLink}
                  >
                    <FiPlus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Progress Indicator */}
      <ProgressIndicator formData={formData} />
      
      {/* Suggestion Banners */}
      <SuggestionBanner type="summary" data={formData.summary} />
      <SuggestionBanner type="experience" data={formData.experience} />
      <SuggestionBanner type="education" data={formData.education} />
      <SuggestionBanner type="skills" data={formData.skills} />
      
      {/* Summary */}
      <div className="mb-8">
        <div className="flex items-center justify-between border-b border-gray-300 pb-1 mb-2">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <span className="w-2 h-4 bg-primary-500 rounded-sm mr-2"></span>
            Summary
          </h2>
          <div className="group relative">
            <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
              Keep it concise and highlight your key strengths
              <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-800"></div>
            </div>
          </div>
        </div>
        <div className="relative">
          <InlineEditor
            value={formData.summary}
            onSave={(value) => updateField('summary', value)}
            multiline
            className="w-full p-3 rounded"
            placeholder="Click to add a professional summary..."
          />
          {formData.summary && (
            <div className="absolute bottom-2 right-2 text-xs text-gray-400">
              {formData.summary.length}/{SUGGESTIONS.summary.maxLength} characters
            </div>
          )}
        </div>
      </div>
      

      
      {/* Dynamic Sections based on order */}
      {(formData.sectionOrder || ['education', 'skills', 'experience', 'activities', 'certificates']).map(section => {
        switch(section) {
          case 'skills':
            return (
              <SkillsSection 
                key="skills-section"
                skills={formData.skills || []}
                languages={formData.languages || []}
                onUpdate={(skills) => updateField('skills', skills)}
                onUpdateLanguages={(languages) => updateField('languages', languages)}
              />
            );
          case 'experience':
            return (
              <ExperienceSection 
                key="experience-section"
                experience={formData.experience || []}
                onUpdate={(experience) => updateField('experience', experience)}
              />
            );
          case 'education':
            return (
              <EducationSection 
                key="education-section"
                education={formData.education || []}
                onUpdate={(education) => updateField('education', education)}
              />
            );
          case 'certificates':
            return (
              <CertificatesSection 
                key="certificates-section"
                certificates={formData.certificates || []}
                onUpdate={(certificates) => updateField('certificates', certificates)}
              />
            );
          case 'activities':
            return (
              <ActivitiesSection
                key="activities-section"
                activities={formData.activities || []}
                onUpdate={(activities) => updateField('activities', activities)}
              />
            );
          default:
            return null;
        }
      })}
      
      {/* Floating Save Button - Hidden when embedded */}
      {!isEmbedded && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-5 py-2.5 border border-transparent shadow-lg text-sm font-medium rounded-full transition-all text-white bg-primary-600 hover:bg-primary-700"
          >
            <FiSave className="-ml-1 mr-2 h-5 w-5" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Action Tooltip */}
      <ResumeActionTooltip 
        show={showActionTooltip} 
        onDownload={() => setShowStyleModal(true)}
        resumeId={resumeId}
      />
      
      {/* Resume Style Modal */}
      <ResumeStyleModal 
        show={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        resumeId={resumeId}
        versionId={versionId}
      />
      
      {/* Section Order Modal */}
      {showSectionOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96 max-w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Re-order Resume Sections</h3>
              <button 
                onClick={() => setShowSectionOrderModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Drag and drop sections to change their order on your resume.
            </p>
            <DraggableContainer className="space-y-2 mb-4">
              {(formData.sectionOrder || ['education', 'skills', 'experience', 'activities', 'certificates']).map((section, index) => (
                <DraggableItem
                  key={`section-${section}`}
                  dragId={`section-${section}`}
                  index={index}
                  onReorder={(sourceIndex, destinationIndex) => {
                    const updatedOrder = [...(formData.sectionOrder || ['education', 'skills', 'experience', 'activities', 'certificates'])];
                    const [removed] = updatedOrder.splice(sourceIndex, 1);
                    updatedOrder.splice(destinationIndex, 0, removed);
                    updateField('sectionOrder', updatedOrder);
                  }}
                  className="bg-white border border-gray-200 rounded-md p-3 flex items-center"
                >
                  {() => (
                    <>
                      <span className="mr-3 text-gray-400">
                        <FiMove className="h-4 w-4" />
                      </span>
                      <span className="font-medium text-gray-800 capitalize">
                        {section === 'certificates' ? 'Certifications' : section}
                      </span>
                    </>
                  )}
                </DraggableItem>
              ))}
            </DraggableContainer>
            <div className="flex justify-end">
              <button
                onClick={() => setShowSectionOrderModal(false)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-all duration-200 hover:shadow-md active:scale-[0.98]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ResumeDocument;